import { ReactNode } from 'react';
import { StatusMark, WarningIcon } from './Pill';

/** What is missing in ink, then the way forward in ink-2. No box, no illustration. */
export function EmptyState({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="py-8 max-w-[72ch]">
      <p className="text-[15px] leading-[22px] text-ink">{title}</p>
      <div className="text-[15px] leading-[22px] text-ink-2 mt-1">{children}</div>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function SkeletonBar({ width, delay = 0 }: { width: string; delay?: number }) {
  return <div className="h-[10px] rounded-sm bg-line animate-pulse" style={{ width, animationDelay: `${delay}s` }} />;
}

export function SkeletonRows({
  rows = 8,
  widths = ['22%', '14%', '16%', '12%'],
}: {
  rows?: number;
  widths?: string[];
}) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-6 py-[17px] border-b border-line">
          {widths.map((w, i) => (
            <SkeletonBar key={i} width={w} delay={r * 0.05 + i * 0.15} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function LoadingPanel({ label, height = 280 }: { label: string; height?: number }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3" style={{ height }}>
      <div className="w-[14px] h-[14px] border-2 border-line border-t-ink-2 rounded-full animate-spin" />
      <span className="text-small text-ink-3">{label}</span>
    </div>
  );
}

/** An error or a real problem: say what is wrong and what to do. */
export function ErrorNotice({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex gap-[10px] rounded-sm bg-warning-tint text-warning px-4 py-3 text-[15px] leading-[22px]"
    >
      <span className="mt-1">
        <WarningIcon />
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function InfoNotice({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-[10px] rounded-sm bg-accent-tint text-accent px-4 py-3 text-[15px] leading-[22px] max-w-[72ch]">
      <span className="mt-1">
        <StatusMark mark="info" />
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
